import { Icon } from "@iconify/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Field, Form, Formik } from "formik";
import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { createStripePaymentCheckout, website_api_key } from "../../../api";
import { creditUserPoints, getAvailableExternalPoints } from "../../../api/points";
import { brand } from "../../../context";
import { useGuestProfile } from "../../../hooks/profile";
import { PointsLedger } from "../../../pages/MyProfile/MyProfilePoints";
import { queryClient } from "../../../queryClient";
import { StyleTypeContext } from "../../../umbilical_tools";
import { Loader } from "../../widgets/Loader";

const PointsTopUpModal = ({ onClose }) => {
  const [disclaimerExpanded, setDisclaimerExpanded] = useState(false);
  const [step, setStep] = useState(1);
  const source_entity = useSelector((state) => state?.WebsiteMetadata?.metadata?.source_entity);
  const guestprofile = useGuestProfile();

  const guest_crm_uid = guestprofile?.uuid;
  const secondary_ext_user_id = guestprofile?.secondary_ext_user_id;

  const pointsData = queryClient.getQueryState(["points", guest_crm_uid]);
  const availablePoints = (pointsData?.data?.data?.results ?? []).reduce(
    (a, b) => a + parseFloat(b.amount),
    0,
  );
  const creditMutation = useMutation({
    mutationFn: ({ source_entity, description, currency, amount, guest_crm_uid }) => {
      return createStripePaymentCheckout({
        amount: 99,
        balance_amount: 0,
        product_name: "Points",
        currency: "USD",
        description: "Club to Travily Points Conversion",
        source_entity,
        website_api_key,
        customer_email: null,
        customer_stripe_id: null,
        success_url: window.location.href,
        cancel_url: window.location.href,
        metadata: {
          type: "one_off_fee",
          source_entity,
          description,
          currency,
          amount,
          guest_crm_uid,
          third_party_id: secondary_ext_user_id,
        },
      });
    },
    onSuccess: (data) => {
      toast.success("Points converted successfully", { autoClose: 5000 });
      window.location.href = data.redirect_url;
    },
    onError: () => {
      toast.error("Failed to convert points", { autoClose: 5000 });
    },
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["available_external_points", guestprofile.ext_user_id],
    queryFn: () => getAvailableExternalPoints({ ext_user_id: guestprofile.ext_user_id }),
    refetchOnWindowFocus: false,
    onError: () => {
      toast.error("Failed to fetch available points", { autoClose: 5000 });
      onClose();
    },
  });

  const availableExtPoints = parseInt(data?.data?.available_external_points ?? 0, 10);
  const conversionRate = 17;

  return (
    <div className="Modal PointsTopUpModal">
      <Formik initialValues={{ amount: 0 }}>
        {({ values }) => {
          const convertedPoints = !values.amount ? 0 : parseFloat(values.amount) * conversionRate;

          return (
            <div className="Modal__card PointsTopUpModal__card">
              {creditMutation.isLoading && <Loader size="lg" />}
              <div className="Modal__card__header" data-brand={brand}>
                <h5>Top-up Points</h5>
              </div>
              <div className="Modal__card__body">
                {step === 2 && (
                  <div className="PointsTopUpModal__success">
                    <Icon icon="mdi:tick" className="PointsTopUpModal__success__icon" />
                    <p>
                      You have successfully converted {values.amount} Anantara Vacation Club points
                      to {convertedPoints} Travily Points.
                    </p>
                  </div>
                )}
                <PointsLedger user_uid={guestprofile?.uuid} hideInMobile={disclaimerExpanded} />
                {step === 1 && (
                  <Form className="PointsTopUpModal__form">
                    <p>
                      You have{" "}
                      {isLoading || isFetching ? (
                        <Icon icon="eos-icons:three-dots-loading" />
                      ) : (
                        availableExtPoints
                      )}{" "}
                      Anantara Vacation Club Point
                      {availableExtPoints > 1 ? "s" : ""} available for conversion.
                    </p>
                    <div className="PointsTopUpModal__form__fields">
                      <div className="PointsTopUpModal__form__inputgroup">
                        <label htmlFor="amount">Amount</label>
                        <Field
                          as="input"
                          name="amount"
                          type="number"
                          min={0}
                          className="Input"
                          data-brand={brand}
                        />
                      </div>
                      <div className="PointsTopUpModal__form__inputgroup">
                        <label htmlFor="amount">Travily Points</label>
                        <span className="Input" data-brand={brand}>
                          {convertedPoints.toLocaleString("en-US", {
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </div>
                    {brand === "travily" && (
                      <React.Fragment>
                        <p
                          className="PointsTopUpModal__form__disclaimer"
                          data-expanded={disclaimerExpanded}
                        >
                          1 Anantara Vacation Club Point = 17 Travily Points. Only the current Club
                          Points can be converted at this rate. A USD 99 fee will be charged per
                          conversion.
                          <br />
                          Please note that if you still have unused conversion fee credit from
                          Anantara Vacation Club, the USD 99 fee will be refunded to your Anantara
                          Vacation Club account as club fee credit.
                          <br />
                          There are other conversion options available. To find out more, please
                          contact Club Services at Anantara Vacation Club.
                        </p>
                        <span
                          className="PointsTopUpModal__form__disclaimer-expander"
                          onClick={() => setDisclaimerExpanded((p) => !p)}
                        >
                          {disclaimerExpanded ? "View Less" : "View More"}
                        </span>
                      </React.Fragment>
                    )}
                    <div className="PointsTopUpModal__form__resulting-points" data-brand={brand}>
                      <h5>
                        Resulting Travily Points:{" "}
                        {(availablePoints + convertedPoints).toLocaleString("en-US", {
                          maximumFractionDigits: 2,
                        })}
                      </h5>
                    </div>
                  </Form>
                )}
              </div>
              <div className="Modal__card__actions">
                <button className="Button" data-ghost="true" data-brand={brand} onClick={onClose}>
                  Close
                </button>
                {step === 1 && (
                  <button
                    className="Button"
                    data-brand={brand}
                    onClick={() => {
                      convertedPoints > 0
                        ? creditMutation.mutate({
                            source_entity,
                            description: "Club to Travily Points Conversion",
                            currency: "TRV",
                            amount: convertedPoints,
                            guest_crm_uid: guestprofile.uuid,
                          })
                        : toast.warning("Please enter a valid amount", {
                            autoClose: 5000,
                          });
                    }}
                  >
                    Proceed
                  </button>
                )}
              </div>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};
PointsTopUpModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default PointsTopUpModal;
